import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useLogin, useMe, useSignup } from '@/lib/auth';
import { usePublicSettings } from '@/lib/settings';

import { WelcomeLayout } from '@/components/auth/WelcomeLayout';

import { AuthCard, FormField } from './LoginPage';

export function SignupPage() {
  const me = useMe();
  const settings = usePublicSettings();
  const signup = useSignup();
  const login = useLogin();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  if (me.data) return <Navigate to="/" replace />;

  if (settings.data && settings.data.signup_mode === 'request') {
    return <Navigate to="/request-account" replace />;
  }
  if (settings.data && settings.data.signup_mode === 'closed') {
    return (
      <WelcomeLayout>
        <AuthCard
          standalone={false}
          title="Sign-up is closed"
          subtitle={
            settings.data.signup_disabled_message ??
            'Self-registration is disabled on this server. Ask an administrator for an invitation.'
          }
        >
          <p className="text-center text-sm text-stone-600 dark:text-stone-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-forge-700 dark:text-forge-400 hover:underline">
              Sign in
            </Link>
          </p>
        </AuthCard>
      </WelcomeLayout>
    );
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await signup.mutateAsync({ email, password, name: name.trim() || undefined });
      // Sign-up doesn't auto-login (fastapi-users behavior). Log in immediately
      // so the user lands in the app.
      await login.mutateAsync({ email, password });
      navigate('/');
    } catch {
      /* error surfaced below */
    }
  };

  const error = signup.isError ? (signup.error as Error) : login.isError ? (login.error as Error) : null;
  const pending = signup.isPending || login.isPending;

  return (
    <WelcomeLayout>
      <AuthCard title="Create your workshop" subtitle="A personal org gets created for you on signup." standalone={false}>
        <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Display name (optional)">
          <Input
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
        <FormField label="Email">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Password">
          <PasswordInput
            autoComplete="new-password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>

        {error && <p className="text-sm text-red-600">{error.message}</p>}

        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
        <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-forge-700 dark:text-forge-400 hover:underline">
            Sign in
          </Link>
        </p>
      </AuthCard>
    </WelcomeLayout>
  );
}
